import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, tzHeadersSchema } from '../schemas';

// ============================================================
// Param Schemas
// ============================================================

const projectTagParamsSchema = projectIdParamsSchema.extend({
	tagId: requiredStringSchema,
});

// ============================================================
// Query Schemas
// ============================================================

const dailyTagsStatusesQuerySchema = z.object({
	date: requiredStringSchema,
	dateTo: z.string().optional(),
});

const tagDailyStatusesQuerySchema = z.object({
	date: z.string().optional(),
});

/**
 * `tagIds` arrives in two wire forms and must keep accepting both: d-orca sends
 * repeated `tagIds=` params, the frontend and the tests send `tagIds[]=`. Both
 * survive `qs.parse`, and the preprocessor is what normalises a single value
 * into an array — do not "simplify" it.
 */
const tagsTimesheetQuerySchema = z.object({
	tagIds: z.preprocess((v) => (v == null ? v : Array.isArray(v) ? v : [v]), z.array(z.string()).min(1)),
	fromDate: requiredStringSchema,
	toDate: requiredStringSchema,
});

const tagsAbsenceDatesQuerySchema = z.object({
	fromDate: requiredStringSchema,
	toDate: requiredStringSchema,
});

// ============================================================
// Contract
// ============================================================

export const tagsContract = defineContract(
	'tags',
	{
		getDailyTagsStatusesSummary: route({
			method: 'GET',
			path: '/projects/:projectId/dailyTagsStatusesSummary',
			params: projectIdParamsSchema,
			query: dailyTagsStatusesQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getDailyTagsStatusesSummary',
			tags: ['Tags'],
			summary: 'Get daily tags statuses summary',
		}),
		getTagDailyStatusesSummary: route({
			method: 'GET',
			path: '/projects/:projectId/dailyTagsStatusesSummary/:tagId',
			params: projectTagParamsSchema,
			query: tagDailyStatusesQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getTagDailyStatusesSummary',
			tags: ['Tags'],
			summary: 'Get single tag daily statuses summary',
		}),
		getTagsTimesheetV1: route({
			method: 'GET',
			path: '/projects/:projectId/tagsTimesheet',
			params: projectIdParamsSchema,
			query: tagsTimesheetQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getTagsTimesheetV1',
			tags: ['Tags'],
			summary: 'Get tags timesheet (v1)',
		}),
		getTagsTimesheetV2: route({
			method: 'GET',
			path: '/projects/:projectId/tagsTimesheet/v2',
			params: projectIdParamsSchema,
			query: tagsTimesheetQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getTagsTimesheetV2',
			tags: ['Tags'],
			summary: 'Get tags timesheet (v2)',
		}),
		getTagsAbsenceDates: route({
			method: 'GET',
			path: '/projects/:projectId/tagsAbsenceDatesBetweenDates/:tagId',
			params: projectTagParamsSchema,
			query: tagsAbsenceDatesQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getTagsAbsenceDates',
			tags: ['Tags'],
			summary: 'Get tag absence dates between dates',
		}),
	},
	{ root: true }
);
